import { Alert, Bullseye, Button, Content, Flex, FlexItem, Spinner } from '@patternfly/react-core';
import Keycloak, { KeycloakConfig } from 'keycloak-js';
import React, { FC, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { loadKeycloakConfig } from './keycloakConfig';

const KEYCLOAK_TIMEOUT_MS = 15000;
const IFRAME_RECEIVE_TIMEOUT = 10000;

// Custom error class to handle our metadata cleanly
class AuthError extends Error {
  phase: string;
  cause?: unknown;

  constructor(message: string, phase: string, cause?: unknown) {
    super(message);
    this.name = 'AuthError';
    this.phase = phase;
    this.cause = cause;
  }
}

const detectCookieBlocking = async (): Promise<boolean> => {
  if (!navigator.cookieEnabled) return true;
  if (document.hasStorageAccess) {
    try {
      return !(await document.hasStorageAccess());
    } catch {
      return true;
    }
  }
  return false;
};

type KeycloakContextProps = {
  keycloak: Keycloak;
  logout: () => void;
};

export const KeycloakContext = React.createContext<KeycloakContextProps | null>(null);

const isValidContext = (value: any): value is KeycloakContextProps => {
  return value !== null && typeof value === 'object' && 'keycloak' in value;
};

export const useKeycloak = (): KeycloakContextProps => {
  const value = useContext(KeycloakContext);

  if (!isValidContext(value)) {
    throw new Error('useKeycloak must be used within KeycloakAuth');
  }

  return value;
};

export const KeycloakAuth: FC<PropsWithChildren<{ config?: KeycloakConfig }>> = (props) => {
  const [authenticated, setAuthenticated] = useState(false);
  const [instance, setInstance] = useState<Keycloak | null>(null);
  const [error, setError] = useState<{ message: string; phase?: string } | null>(null);
  const { t } = useTranslation();

  const keycloakInstanceRef = useRef<Keycloak | null>(null);
  const initializationPromiseRef = useRef<Promise<boolean> | null>(null);
  const channelRef = useRef<BroadcastChannel | null>(null);

  const logout = useCallback((isBroadcast = false) => {
    initializationPromiseRef.current = null;
    if (!isBroadcast && channelRef.current) {
      channelRef.current.postMessage('logout');
    }
    if (keycloakInstanceRef.current) {
      keycloakInstanceRef.current.logout({ redirectUri: globalThis.location.origin + "/logout-redirect" });
      keycloakInstanceRef.current = null;
    }
  }, []);

  const bindEvents = useCallback((kc: Keycloak) => {
    kc.onReady = (isAuth) => console.debug("Keycloak Ready. Authenticated:", isAuth);
    kc.onAuthSuccess = () => console.debug("Keycloak Auth Success");
    kc.onAuthError = (err) => console.error("Keycloak Auth Error:", err);
    kc.onTokenExpired = () => {
      kc.updateToken(30).catch(() => {
        console.error("Token refresh failed, logging out...");
        logout();
      });
    };
    kc.onAuthLogout = () => logout(true);
  }, [logout]);

  const contextValue = useMemo<KeycloakContextProps>(() => {
    return {
      keycloak: (instance ?? keycloakInstanceRef.current) as Keycloak,
      logout
    };
  }, [instance, logout]);

  useEffect(() => {
    const channel = new BroadcastChannel('auth_channel');
    channel.onmessage = (event) => { if (event.data === 'logout') logout(true); };
    channelRef.current = channel;
    return () => {
      channel.close();
      channelRef.current = null;
    };
  }, [logout]);

  useEffect(() => {
    if (authenticated || error) return;

    const controller = new AbortController();
    const { signal } = controller;

    const timeoutPromise = new Promise<never>((_, reject) =>
      setTimeout(() => reject(new AuthError('Authentication timed out', 'TIMEOUT')), KEYCLOAK_TIMEOUT_MS)
    );

    const startAuth = async () => {
      const performInit = async (): Promise<boolean> => {
        try {
          const isBlocked = await detectCookieBlocking();

          let config: KeycloakConfig;
          try {
            config = props.config ?? await loadKeycloakConfig(signal);
          } catch (e) {
            throw new AuthError('Failed to load config', 'CONFIG_LOAD', e);
          }

          if (!keycloakInstanceRef.current) {
            keycloakInstanceRef.current = new Keycloak(config);
            bindEvents(keycloakInstanceRef.current);
          }

          let isAuth = false;
          try {
            isAuth = await keycloakInstanceRef.current.init({
              onLoad: 'login-required',
              checkLoginIframe: !isBlocked,
              pkceMethod: 'S256',
              redirectUri: globalThis.location.href,
              messageReceiveTimeout: IFRAME_RECEIVE_TIMEOUT
            });
          } catch (e) {
            throw new AuthError('Keycloak init failed', isBlocked ? 'COOKIE_BLOCK' : 'KEYCLOAK_INIT', e);
          }

          if (isAuth) {
            try {
              await Promise.all([
                keycloakInstanceRef.current.loadUserProfile(),
                keycloakInstanceRef.current.loadUserInfo()
              ]);
            } catch (e) {
              throw new AuthError('User profile load failed', 'USER_PROFILE', e);
            }
            return true;
          }
          return false;
        } catch (err: any) {
          if (err?.cause?.name === 'AbortError' || err?.name === 'AbortError') return false;
          throw err;
        }
      };

      initializationPromiseRef.current ??= performInit();

      try {
        const success = await Promise.race([initializationPromiseRef.current, timeoutPromise]);

        if (success && !signal.aborted) {
          setInstance(keycloakInstanceRef.current);
          setAuthenticated(true);
        }
      } catch (err: any) {
        initializationPromiseRef.current = null;
        if (!signal.aborted) {
          const phaseLabel = err instanceof AuthError ? err.phase : 'UNKNOWN';
          const errorMap: Record<string, string> = {
            CONFIG_LOAD: t('keycloakInstance.errConfig', 'Failed to load authentication configuration.'),
            KEYCLOAK_INIT: t('keycloakInstance.errInit', 'Failed to initialize the identity provider.'),
            USER_PROFILE: t('keycloakInstance.errProfile', 'Failed to load user profile information.'),
            TIMEOUT: t('keycloakInstance.errTimeout', 'The authentication process timed out.'),
            COOKIE_BLOCK: t('keycloakInstance.errCookies', 'Authentication failed. Please ensure third-party cookies are enabled in your browser settings.')
          };
          setError({ message: errorMap[phaseLabel] || t('keycloakInstance.errGeneral', 'Authentication error.'), phase: phaseLabel });
        }
      }
    };

    startAuth();
    return () => controller.abort();
  }, [authenticated, props.config, logout, bindEvents, t]);

  // Success
  if (authenticated && instance) {
    return <KeycloakContext.Provider value={contextValue}>{props.children}</KeycloakContext.Provider>;
  }

  // Error
  if (error) {
    return (
      <div style={{ height: '100vh', backgroundColor: 'var(--pf-t--global--background--color--primary--default)' }}>
        <Bullseye>
          <Flex direction={{ default: 'column' }} alignItems={{ default: 'alignItemsCenter' }} style={{ maxWidth: '600px' }}>
            <FlexItem>
              <Alert variant="danger" isInline title={t('keycloakInstance.errorTitle', 'Authentication Error')}>
                <Flex direction={{ default: 'column' }} alignItems={{ default: 'alignItemsCenter' }} spaceItems={{ default: 'spaceItemsMd' }}>
                  <FlexItem>
                    <p>{error.message}</p>
                    <p style={{ fontSize: 'var(--pf-t--global--font--size--sm)', opacity: 0.8, marginTop: 'var(--pf-t--global--spacer--xs)' }}>
                      {t('keycloakInstance.errorCode', 'Error Code')}: {error.phase}
                    </p>
                  </FlexItem>
                  <FlexItem>
                    <Button
                      variant="primary"
                      onClick={() => {
                        keycloakInstanceRef.current = null;
                        initializationPromiseRef.current = null;
                        setError(null);
                      }}
                    >
                      {t('keycloakInstance.retry', 'Retry Login')}
                    </Button>
                  </FlexItem>
                </Flex>
              </Alert>
            </FlexItem>
          </Flex>
        </Bullseye>
      </div>
    );
  }

  // Loading
  return (
    <div style={{ height: '100vh', backgroundColor: 'var(--pf-t--global--background--color--primary--default)' }}>
      <Bullseye>
        <Flex direction={{ default: 'column' }} alignItems={{ default: 'alignItemsCenter' }} spaceItems={{ default: 'spaceItemsMd' }}>
          <FlexItem><Spinner size="xl" aria-label={t('keycloakInstance.loadingAuth', 'Loading authentication')} /></FlexItem>
          <FlexItem>
            <Content>
              <p style={{ color: 'var(--pf-t--global--text--color--regular)', fontWeight: 'var(--pf-t--global--font--weight--default)' }}>
                {t('keycloakInstance.waitMessage', 'Please wait while we authenticate your session...')}
              </p>
            </Content>
          </FlexItem>
        </Flex>
      </Bullseye>
    </div>
  );
};
